import { useState, useMemo } from 'react';
import { demoRooms, demoGuests, demoBookings, demoRoomBeds, demoBedBookings } from '@/mocks/demoData';
import type { Booking, RoomBed, Payment } from '@/mocks/data';
import { useApp } from '@/contexts/AppContext';
import { addPayment, generateReceiptNo, generatePaymentId } from '@/utils/paymentStore';

const BED_STATUS_COLORS: Record<RoomBed['status'], string> = {
  available: 'bg-green-100 border-green-300 text-green-700 hover:bg-green-200',
  occupied: 'bg-red-100 border-red-300 text-red-400 cursor-not-allowed',
  reserved: 'bg-yellow-100 border-yellow-300 text-yellow-400 cursor-not-allowed',
  cleaning: 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed',
  maintenance: 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed',
};

export default function BookingNew() {
  const { addToast } = useApp();
  const [step, setStep] = useState(1);
  const [searchGuest, setSearchGuest] = useState('');
  const [selectedGuest, setSelectedGuest] = useState<typeof demoGuests[0] | null>(null);
  const [isNewGuest, setIsNewGuest] = useState(false);
  const [guestForm, setGuestForm] = useState({
    full_name: '', phone: '', email: '', id_proof_type: 'Aadhaar', id_proof_number: '', address: ''
  });
  const [bookingMode, setBookingMode] = useState<'full_room' | 'bed'>('full_room');
  const [selectedBeds, setSelectedBeds] = useState<string[]>([]);
  const [bookingForm, setBookingForm] = useState({
    room_id: '',
    check_in: '',
    check_out: '',
    booking_type: 'walk_in' as Booking['booking_type'],
    deposit: '',
    extra_charges: [] as { name: string; amount: number }[],
  });
  const [extraName, setExtraName] = useState('');
  const [extraAmount, setExtraAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<Payment['payment_method']>('cash');

  // Get rooms available for full booking (private rooms that are available)
  const availablePrivateRooms = useMemo(() =>
    demoRooms.filter((r) => r.room_type === 'private' && r.status === 'available'),
  []);

  // Get shared/dormitory rooms
  const sharedDormRooms = useMemo(() =>
    demoRooms.filter((r) => r.room_type === 'shared' || r.room_type === 'dormitory'),
  []);

  const allAvailableRooms = useMemo(() =>
    demoRooms.filter((r) => r.status === 'available'),
  []);

  const selectedRoom = useMemo(() => {
    if (bookingMode === 'full_room') {
      return allAvailableRooms.find((r) => r.id === bookingForm.room_id);
    }
    return sharedDormRooms.find((r) => r.id === bookingForm.room_id);
  }, [bookingForm.room_id, bookingMode]);

  const filteredGuests = useMemo(() => {
    if (!searchGuest) return [];
    return demoGuests.filter((g) =>
      g.full_name.toLowerCase().includes(searchGuest.toLowerCase()) ||
      g.phone.includes(searchGuest) ||
      g.id_proof_number.includes(searchGuest)
    );
  }, [searchGuest]);

  const nights = useMemo(() => {
    if (!bookingForm.check_in || !bookingForm.check_out) return 0;
    const start = new Date(bookingForm.check_in);
    const end = new Date(bookingForm.check_out);
    const diff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  }, [bookingForm.check_in, bookingForm.check_out]);

  // Pricing logic
  const getPriceForRoom = (room: typeof selectedRoom) => {
    if (!room) return { roomTotal: 0, perUnit: '', unitCount: 0 };
    if (bookingMode === 'bed') {
      return {
        roomTotal: room.per_bed_price * selectedBeds.length * nights,
        perUnit: 'bed',
        unitCount: selectedBeds.length,
      };
    }
    return {
      roomTotal: nights * room.tariff,
      perUnit: 'night',
      unitCount: nights,
    };
  };

  const pricing = getPriceForRoom(selectedRoom);
  const roomTotal = pricing.roomTotal;
  const extrasTotal = bookingForm.extra_charges.reduce((s, e) => s + e.amount, 0);
  const deposit = Number(bookingForm.deposit) || 0;
  const grandTotal = roomTotal + extrasTotal;
  const pending = grandTotal - deposit;

  const getBedsForRoom = (roomId: string) => demoRoomBeds.filter((b) => b.room_id === roomId);

  const toggleBed = (bedId: string, bed: RoomBed) => {
    if (bed.status !== 'available') return;
    setSelectedBeds((prev) =>
      prev.includes(bedId) ? prev.filter((id) => id !== bedId) : [...prev, bedId]
    );
  };

  const handleRoomSelect = (roomId: string) => {
    setBookingForm((p) => ({ ...p, room_id: roomId }));
    setSelectedBeds([]);
  };

  const addExtra = () => {
    if (!extraName || !extraAmount) return;
    setBookingForm((p) => ({ ...p, extra_charges: [...p.extra_charges, { name: extraName, amount: Number(extraAmount) }] }));
    setExtraName(''); setExtraAmount('');
  };

  const removeExtra = (idx: number) => {
    setBookingForm((p) => ({ ...p, extra_charges: p.extra_charges.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = () => {
    if (!selectedRoom) { addToast('error', 'Select a room'); return; }
    if (!isNewGuest && !selectedGuest) { addToast('error', 'Select or create a guest'); return; }
    if (isNewGuest && (!guestForm.full_name || !guestForm.phone)) { addToast('error', 'Guest name and phone are required'); return; }
    if (!bookingForm.check_in || !bookingForm.check_out) { addToast('error', 'Check-in and check-out dates required'); return; }
    if (nights <= 0) { addToast('error', 'Check-out must be after check-in'); return; }
    if (bookingMode === 'bed' && selectedBeds.length === 0) { addToast('error', 'Select at least one bed'); return; }

    const bookingId = `bk-${Date.now()}`;
    const newBookingId = `HOTEL-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${String(demoBookings.length + 1).padStart(3, '0')}`;

    // Create payment record if deposit > 0
    if (deposit > 0) {
      const receiptNo = generateReceiptNo();
      const payment: Payment = {
        id: generatePaymentId(),
        booking_id: bookingId,
        amount: deposit,
        payment_method: paymentMethod,
        payment_type: deposit >= grandTotal ? 'full' : 'deposit',
        receipt_no: receiptNo,
        created_by: 'usr-003',
        created_at: new Date().toISOString(),
      };
      addPayment(payment);
      addToast('success', `Receipt ${receiptNo} generated — ${paymentMethod.toUpperCase()}`);
    }

    const msg = bookingMode === 'bed'
      ? `Bed booking created: ${newBookingId} (${selectedBeds.length} beds)`
      : `Booking created: ${newBookingId}`;
    addToast('success', msg);

    setStep(1); setSearchGuest(''); setSelectedGuest(null); setIsNewGuest(false);
    setGuestForm({ full_name: '', phone: '', email: '', id_proof_type: 'Aadhaar', id_proof_number: '', address: '' });
    setBookingForm({ room_id: '', check_in: '', check_out: '', booking_type: 'walk_in', deposit: '', extra_charges: [] });
    setBookingMode('full_room'); setSelectedBeds([]);
    setPaymentMethod('cash');
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-navy-800">New Booking</h1>
        <p className="text-navy-500 mt-1">Create walk-in, advance, or pre-booking reservations — full room or bed-wise.</p>
      </div>

      <div className="flex items-center gap-4 mb-6">
        <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${step === 1 ? 'bg-navy-700 text-white' : 'bg-navy-100 text-navy-600'}`}>
          <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">1</span> Guest
        </div>
        <div className="h-px flex-1 bg-navy-200" />
        <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${step === 2 ? 'bg-navy-700 text-white' : step > 2 ? 'bg-green-100 text-green-700' : 'bg-navy-100 text-navy-600'}`}>
          <span className={`w-5 h-5 rounded-full ${step > 2 ? 'bg-green-500 text-white' : 'bg-white/20'} flex items-center justify-center text-xs font-bold`}>{step > 2 ? <i className="ri-check-line" /> : '2'}</span> Booking Details
        </div>
        <div className="h-px flex-1 bg-navy-200" />
        <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${step === 3 ? 'bg-navy-700 text-white' : 'bg-navy-100 text-navy-600'}`}>
          <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">3</span> Summary
        </div>
      </div>

      {step === 1 && (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl border border-navy-100 p-6">
              <h3 className="font-semibold text-navy-800 mb-4">Find Existing Guest</h3>
              <div className="relative mb-4">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-navy-400" />
                <input type="text" placeholder="Search by name, phone, or ID proof..." value={searchGuest} onChange={(e) => { setSearchGuest(e.target.value); setSelectedGuest(null); }} className="w-full pl-9 pr-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" />
              </div>
              {filteredGuests.length > 0 && !selectedGuest && (
                <div className="space-y-2">
                  {filteredGuests.map((g) => (
                    <button key={g.id} onClick={() => { setSelectedGuest(g); setIsNewGuest(false); }} className="w-full text-left flex items-center justify-between p-3 border border-navy-100 rounded-lg hover:bg-navy-50 transition-colors cursor-pointer">
                      <div>
                        <div className="font-medium text-navy-800">{g.full_name} {g.is_repeating && <span className="ml-2 text-xs bg-gold-100 text-gold-700 px-2 py-0.5 rounded-full">Repeat Guest</span>}</div>
                        <div className="text-xs text-navy-500">{g.phone} | {g.id_proof_type}: {g.id_proof_number}</div>
                      </div>
                      <i className="ri-arrow-right-s-line text-navy-400" />
                    </button>
                  ))}
                </div>
              )}
              {searchGuest && filteredGuests.length === 0 && !selectedGuest && (
                <div className="text-sm text-navy-500 py-4 text-center">No guests found. Create a new guest below.</div>
              )}
              {selectedGuest && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-medium text-navy-800">{selectedGuest.full_name}</div>
                    <button onClick={() => setSelectedGuest(null)} className="text-xs text-navy-500 hover:text-navy-700 cursor-pointer">Change</button>
                  </div>
                  <div className="text-xs text-navy-600">{selectedGuest.phone} | {selectedGuest.id_proof_type}: {selectedGuest.id_proof_number}</div>
                </div>
              )}
              <div className="mt-4 pt-4 border-t border-navy-100">
                <button onClick={() => { setIsNewGuest(true); setSelectedGuest(null); }} className={`text-sm font-medium ${isNewGuest ? 'text-navy-700' : 'text-navy-500 hover:text-navy-700'} cursor-pointer`}>
                  <i className={`ri-${isNewGuest ? 'checkbox-circle' : 'add-circle'}-line mr-1`} /> Create New Guest
                </button>
              </div>
              {isNewGuest && (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-navy-500 mb-1">Full Name *</label>
                    <input type="text" className="w-full px-3 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={guestForm.full_name} onChange={(e) => setGuestForm((p) => ({ ...p, full_name: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-navy-500 mb-1">Phone *</label>
                    <input type="text" className="w-full px-3 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={guestForm.phone} onChange={(e) => setGuestForm((p) => ({ ...p, phone: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-navy-500 mb-1">Email</label>
                    <input type="email" className="w-full px-3 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={guestForm.email} onChange={(e) => setGuestForm((p) => ({ ...p, email: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-navy-500 mb-1">ID Proof Type</label>
                    <select className="w-full px-3 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={guestForm.id_proof_type} onChange={(e) => setGuestForm((p) => ({ ...p, id_proof_type: e.target.value }))}>
                      <option>Aadhaar</option><option>Passport</option><option>Driving License</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-navy-500 mb-1">ID Proof Number</label>
                    <input type="text" className="w-full px-3 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={guestForm.id_proof_number} onChange={(e) => setGuestForm((p) => ({ ...p, id_proof_number: e.target.value }))} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-navy-500 mb-1">Address</label>
                    <textarea rows={2} className="w-full px-3 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500 resize-none" value={guestForm.address} onChange={(e) => setGuestForm((p) => ({ ...p, address: e.target.value }))} />
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-navy-100 p-5">
              <h4 className="font-medium text-navy-800 mb-3">Booking Summary</h4>
              <div className="text-sm text-navy-500">Select guest to proceed</div>
            </div>
            <button onClick={() => {
              if (!isNewGuest && !selectedGuest) { addToast('error', 'Select a guest first'); return; }
              if (isNewGuest && (!guestForm.full_name || !guestForm.phone)) { addToast('error', 'Name and phone required'); return; }
              setStep(2);
            }} className="w-full px-4 py-3 bg-navy-700 text-white rounded-lg hover:bg-navy-800 transition-colors cursor-pointer font-medium">
              Next: Booking Details <i className="ri-arrow-right-line ml-1" />
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Booking Mode Selection */}
            <div className="bg-white rounded-xl border border-navy-100 p-6">
              <h3 className="font-semibold text-navy-800 mb-4">Booking Mode</h3>
              <div className="flex gap-3">
                <button
                  onClick={() => { setBookingMode('full_room'); setBookingForm((p) => ({ ...p, room_id: '' })); setSelectedBeds([]); }}
                  className={`flex-1 px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all cursor-pointer ${bookingMode === 'full_room' ? 'border-navy-700 bg-navy-50 text-navy-800' : 'border-navy-100 text-navy-500 hover:border-navy-300'}`}
                >
                  <i className="ri-home-4-line text-lg block mb-1" />
                  Full Room Booking
                </button>
                <button
                  onClick={() => { setBookingMode('bed'); setBookingForm((p) => ({ ...p, room_id: '' })); setSelectedBeds([]); }}
                  className={`flex-1 px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all cursor-pointer ${bookingMode === 'bed' ? 'border-gold-500 bg-gold-50 text-gold-800' : 'border-navy-100 text-navy-500 hover:border-navy-300'}`}
                >
                  <i className="ri-hotel-bed-line text-lg block mb-1" />
                  Bed Booking
                </button>
              </div>
            </div>

            {/* Room Selection - Full Room Mode */}
            {bookingMode === 'full_room' && (
              <div className="bg-white rounded-xl border border-navy-100 p-6">
                <h3 className="font-semibold text-navy-800 mb-4">Select Room</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                  {allAvailableRooms.map((r) => (
                    <button key={r.id} onClick={() => handleRoomSelect(r.id)} className={`text-left p-3 border rounded-lg transition-all cursor-pointer ${bookingForm.room_id === r.id ? 'border-gold-500 bg-gold-50' : 'border-navy-100 hover:border-navy-300'}`}>
                      <div className="font-semibold text-navy-800">Room {r.room_number}</div>
                      <div className="text-xs text-navy-500">{r.category} | Floor {r.floor}</div>
                      <div className="text-sm font-medium text-navy-700 mt-1">Rs. {r.tariff.toLocaleString('en-IN')} <span className="text-xs font-normal text-navy-400">/night</span></div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Room Selection - Bed Booking Mode */}
            {bookingMode === 'bed' && (
              <div className="bg-white rounded-xl border border-navy-100 p-6">
                <h3 className="font-semibold text-navy-800 mb-4">Select Room and Beds</h3>
                {/* Room selector */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                  {sharedDormRooms.map((r) => {
                    const beds = getBedsForRoom(r.id);
                    const availBeds = beds.filter((b) => b.status === 'available').length;
                    return (
                      <button key={r.id} onClick={() => handleRoomSelect(r.id)} className={`text-left p-3 border rounded-lg transition-all cursor-pointer ${bookingForm.room_id === r.id ? 'border-gold-500 bg-gold-50' : 'border-navy-100 hover:border-navy-300'}`}>
                        <div className="font-semibold text-navy-800">Room {r.room_number}</div>
                        <div className="text-xs text-navy-500">{r.category} | {r.total_beds} beds</div>
                        <div className="text-xs text-green-600 mt-0.5">{availBeds} beds available</div>
                        <div className="text-sm font-medium text-navy-700 mt-1">Rs. {r.per_bed_price.toLocaleString('en-IN')} <span className="text-xs font-normal text-navy-400">/bed</span></div>
                      </button>
                    );
                  })}
                </div>

                {/* Bed Layout Selection */}
                {selectedRoom && bookingMode === 'bed' && (
                  <div>
                    <h4 className="text-sm font-medium text-navy-700 mb-3">
                      Select Beds — Room {selectedRoom.room_number}
                      <span className="text-xs font-normal text-navy-400 ml-2">
                        {selectedBeds.length} selected
                      </span>
                    </h4>
                    <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                      {getBedsForRoom(selectedRoom.id).sort((a, b) => a.bed_number - b.bed_number).map((bed) => {
                        const isSelected = selectedBeds.includes(bed.id);
                        const isAvailable = bed.status === 'available';
                        return (
                          <button
                            key={bed.id}
                            onClick={() => toggleBed(bed.id, bed)}
                            disabled={!isAvailable}
                            className={`p-3 rounded-lg border-2 text-center text-sm font-medium transition-all cursor-pointer
                              ${isSelected ? 'border-gold-500 bg-gold-100 text-gold-800' : BED_STATUS_COLORS[bed.status]}
                              ${isAvailable && !isSelected ? 'border-navy-200 bg-green-50 text-green-700 hover:border-navy-400' : ''}
                            `}
                          >
                            <i className={`ri-hotel-bed-line text-lg block mb-0.5 ${isSelected ? 'text-gold-600' : ''}`} />
                            Bed {bed.bed_number}
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex flex-wrap gap-3 mt-4 text-xs">
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-green-500" /> Available</span>
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-500" /> Occupied</span>
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-yellow-500" /> Reserved</span>
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-gray-400" /> Cleaning</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="bg-white rounded-xl border border-navy-100 p-6">
              <h3 className="font-semibold text-navy-800 mb-4">Dates & Type</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-navy-500 mb-1">Check-in *</label>
                  <input type="datetime-local" className="w-full px-3 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={bookingForm.check_in} onChange={(e) => setBookingForm((p) => ({ ...p, check_in: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-navy-500 mb-1">Check-out *</label>
                  <input type="datetime-local" className="w-full px-3 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={bookingForm.check_out} onChange={(e) => setBookingForm((p) => ({ ...p, check_out: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-navy-500 mb-1">Booking Type</label>
                  <select className="w-full px-3 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={bookingForm.booking_type} onChange={(e) => setBookingForm((p) => ({ ...p, booking_type: e.target.value as Booking['booking_type'] }))}>
                    <option value="walk_in">Walk-in</option>
                    <option value="advance">Advance</option>
                    <option value="pre_booking">Pre-Booking</option>
                    <option value="online">Online</option>
                  </select>
                </div>
              </div>
              <h3 className="font-semibold text-navy-800 mb-4 mt-6">Extra Charges</h3>
              <div className="flex gap-3 mb-3">
                <input type="text" placeholder="Item name (e.g. Extra Bed)" className="flex-1 px-3 py-2 border border-navy-200 rounded-lg text-sm" value={extraName} onChange={(e) => setExtraName(e.target.value)} />
                <input type="number" placeholder="Amount" className="w-32 px-3 py-2 border border-navy-200 rounded-lg text-sm" value={extraAmount} onChange={(e) => setExtraAmount(e.target.value)} />
                <button onClick={addExtra} className="px-4 py-2 bg-navy-700 text-white rounded-lg text-sm hover:bg-navy-800 transition-colors cursor-pointer"><i className="ri-add-line" /></button>
              </div>
              {bookingForm.extra_charges.length > 0 && (
                <div className="space-y-2">
                  {bookingForm.extra_charges.map((e, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-navy-50 rounded-lg text-sm">
                      <span className="text-navy-700">{e.name}</span>
                      <div className="flex items-center gap-3">
                        <span className="font-medium text-navy-800">Rs. {e.amount.toLocaleString('en-IN')}</span>
                        <button onClick={() => removeExtra(i)} className="text-red-500 hover:text-red-700 cursor-pointer"><i className="ri-close-line" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-navy-100 p-5">
              <h4 className="font-medium text-navy-800 mb-3">Booking Summary</h4>
              {selectedRoom ? (
                <>
                  <div className="flex justify-between text-sm mb-2"><span className="text-navy-500">Mode</span><span className="text-navy-800 font-medium">{bookingMode === 'bed' ? 'Bed Booking' : 'Full Room'}</span></div>
                  <div className="flex justify-between text-sm mb-2"><span className="text-navy-500">Room</span><span className="text-navy-800">{selectedRoom.room_number} ({selectedRoom.category})</span></div>
                  {bookingMode === 'bed' && (
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-navy-500">Beds</span>
                      <span className="text-navy-800">{selectedBeds.length > 0 ? selectedBeds.map((bid) => {
                        const b = demoRoomBeds.find((x) => x.id === bid);
                        return b ? `B${b.bed_number}` : '';
                      }).join(', ') : 'None selected'}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm mb-2"><span className="text-navy-500">Nights</span><span className="text-navy-800">{nights}</span></div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-navy-500">{bookingMode === 'bed' ? 'Bed Charges' : 'Room Charges'}</span>
                    <span className="text-navy-800">Rs. {roomTotal.toLocaleString('en-IN')}</span>
                  </div>
                  {bookingMode === 'bed' && selectedRoom && selectedBeds.length > 0 && (
                    <div className="text-xs text-navy-400 mb-2">{selectedBeds.length} {selectedBeds.length === 1 ? 'bed' : 'beds'} x Rs. {selectedRoom.per_bed_price.toLocaleString('en-IN')} x {nights} {nights === 1 ? 'night' : 'nights'}</div>
                  )}
                  {extrasTotal > 0 && <div className="flex justify-between text-sm mb-2"><span className="text-navy-500">Extras</span><span className="text-navy-800">Rs. {extrasTotal.toLocaleString('en-IN')}</span></div>}
                  <div className="border-t border-navy-100 my-3" />
                  <div className="flex justify-between font-semibold text-navy-800"><span>Total</span><span>Rs. {grandTotal.toLocaleString('en-IN')}</span></div>
                  <div className="mt-3">
                    <label className="block text-xs font-medium text-navy-500 mb-1">Deposit / Paid</label>
                    <input type="number" className="w-full px-3 py-2 border border-navy-200 rounded-lg text-sm" placeholder="0" value={bookingForm.deposit} onChange={(e) => setBookingForm((p) => ({ ...p, deposit: e.target.value }))} />
                  </div>
                  {Number(bookingForm.deposit) > 0 && (
                    <div className="mt-3">
                      <label className="block text-xs font-medium text-navy-500 mb-1">Payment Method</label>
                      <select
                        className="w-full px-3 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
                        value={paymentMethod}
                        onChange={(e) => setPaymentMethod(e.target.value as Payment['payment_method'])}
                      >
                        <option value="cash">Cash</option>
                        <option value="card">Card</option>
                        <option value="upi">UPI</option>
                        <option value="online">Online</option>
                      </select>
                    </div>
                  )}
                  <div className="flex justify-between text-sm mt-3 pt-3 border-t border-navy-100"><span className="text-navy-500">Pending</span><span className={`font-semibold ${pending > 0 ? 'text-red-600' : 'text-green-600'}`}>Rs. {pending.toLocaleString('en-IN')}</span></div>
                </>
              ) : (
                <div className="text-sm text-navy-400">Select a room to see pricing</div>
              )}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setStep(1)} className="flex-1 px-4 py-3 border border-navy-200 rounded-lg text-sm font-medium text-navy-600 hover:bg-navy-50 transition-colors cursor-pointer">Back</button>
              <button onClick={() => {
                if (!selectedRoom) { addToast('error', 'Select a room'); return; }
                if (!bookingForm.check_in || !bookingForm.check_out) { addToast('error', 'Enter check-in and check-out dates'); return; }
                if (nights <= 0) { addToast('error', 'Check-out must be after check-in'); return; }
                if (bookingMode === 'bed' && selectedBeds.length === 0) { addToast('error', 'Select at least one bed'); return; }
                setStep(3);
              }} className="flex-1 px-4 py-3 bg-navy-700 text-white rounded-lg hover:bg-navy-800 transition-colors cursor-pointer font-medium">Next: Summary</button>
            </div>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-xl border border-navy-100 p-6 mb-6">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-navy-800">Booking Confirmation</h3>
              <p className="text-sm text-navy-500">Review details before confirming</p>
            </div>
            <div className="space-y-4">
              <div className="p-4 bg-navy-50 rounded-lg">
                <div className="text-sm font-medium text-navy-700 mb-2">Guest</div>
                <div className="text-sm text-navy-800">{isNewGuest ? guestForm.full_name : selectedGuest?.full_name}</div>
                <div className="text-xs text-navy-500">{isNewGuest ? guestForm.phone : selectedGuest?.phone}</div>
              </div>
              <div className="p-4 bg-navy-50 rounded-lg">
                <div className="text-sm font-medium text-navy-700 mb-2">Booking Details</div>
                <div className="text-sm text-navy-800">
                  {bookingMode === 'bed' ? 'Bed Booking' : 'Full Room Booking'} — Room {selectedRoom?.room_number} ({selectedRoom?.category})
                </div>
                {bookingMode === 'bed' && selectedBeds.length > 0 && (
                  <div className="text-sm text-navy-600 mt-1">
                    Beds: {selectedBeds.map((bid) => {
                      const b = demoRoomBeds.find((x) => x.id === bid);
                      return b ? `Bed ${b.bed_number}` : '';
                    }).join(', ')}
                  </div>
                )}
                <div className="text-xs text-navy-500 mt-1">
                  {bookingMode === 'bed'
                    ? `${selectedBeds.length} beds x Rs. ${selectedRoom?.per_bed_price.toLocaleString('en-IN')} x ${nights} nights`
                    : `${nights} nights | ${bookingForm.booking_type.replace('_', ' ')}`
                  }
                </div>
              </div>
              <div className="p-4 bg-navy-50 rounded-lg">
                <div className="text-sm font-medium text-navy-700 mb-2">Charges</div>
                <div className="flex justify-between text-sm">
                  <span className="text-navy-600">
                    {bookingMode === 'bed'
                      ? `Bed Charges (${selectedBeds.length} x Rs. ${selectedRoom?.per_bed_price.toLocaleString('en-IN')} x ${nights} nights)`
                      : `Room (${nights} x Rs. ${selectedRoom?.tariff.toLocaleString('en-IN')})`
                    }
                  </span>
                  <span className="text-navy-800">Rs. {roomTotal.toLocaleString('en-IN')}</span>
                </div>
                {bookingForm.extra_charges.map((e, i) => (
                  <div key={i} className="flex justify-between text-sm"><span className="text-navy-600">{e.name}</span><span className="text-navy-800">Rs. {e.amount.toLocaleString('en-IN')}</span></div>
                ))}
                <div className="border-t border-navy-200 my-2" />
                <div className="flex justify-between font-semibold"><span className="text-navy-800">Grand Total</span><span className="text-navy-800">Rs. {grandTotal.toLocaleString('en-IN')}</span></div>
                <div className="flex justify-between text-sm mt-1"><span className="text-navy-600">Deposit Paid</span><span className="text-green-600">Rs. {deposit.toLocaleString('en-IN')}</span></div>
                {deposit > 0 && <div className="flex justify-between text-sm"><span className="text-navy-600">Payment Method</span><span className="text-navy-800 capitalize">{paymentMethod}</span></div>}
                <div className="flex justify-between text-sm"><span className="text-navy-600">Pending</span><span className="text-red-600 font-medium">Rs. {pending.toLocaleString('en-IN')}</span></div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setStep(2)} className="flex-1 px-4 py-3 border border-navy-200 rounded-lg text-sm font-medium text-navy-600 hover:bg-navy-50 transition-colors cursor-pointer">Back</button>
              <button onClick={handleSubmit} className="flex-1 px-4 py-3 bg-gold-500 text-navy-900 rounded-lg hover:bg-gold-600 transition-colors cursor-pointer font-medium">Confirm Booking</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}